// Global Instructions Rule Applied!

import React, { forwardRef } from 'react'
import DatePicker from '../../DatePicker.tsx'
import { InputSize } from '../types.ts'
import { typedDateFormats } from '../../../utils/dateInputFormats.ts'
import dayjs, { Dayjs } from 'dayjs'

export interface DateInputProps {
  value: any
  onChange: (value: any) => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  /**
   * Called when the user presses Enter, with the value the picker holds at
   * that moment (typed text parsed with `format`, falling back to the
   * current value). The picker only commits typed text AFTER the keydown
   * event, so a confirm handler that reads state on Enter sees the stale
   * value — callers should confirm with the value passed here instead (the
   * same override-value pattern the checkbox auto-confirm uses).
   */
  onEnterConfirm?: (value: string | null) => void
  size?: InputSize
  placeholder?: string
  format?: string
  showTime?: boolean
  allowClear?: boolean
  status?: 'error' | 'warning'
  autoFocus?: boolean
}

export const DateInput = forwardRef<any, DateInputProps>(
  (
    {
      value,
      onChange,
      onKeyDown,
      onEnterConfirm,
      size = 'small',
      placeholder,
      format = 'YYYY-MM-DD',
      showTime = false,
      allowClear = true,
      status,
      autoFocus
    },
    ref
  ) => {
    const handleChange = (date: Dayjs | null, dateString: string | string[]) => {
      if (date) {
        onChange(date.format(format))
      } else {
        onChange(null)
      }
    }

    const dayjsValue = value ? dayjs(value) : null

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && onEnterConfirm) {
        const text = (e.target as HTMLInputElement).value
        // the same typed variants the picker itself accepts (no leading
        // zeros, two-digit year, ISO), parsed strictly like the picker does
        const typed = text ? dayjs(text, typedDateFormats(format), true) : null
        const resolved =
          typed && typed.isValid() ? typed : dayjsValue && dayjsValue.isValid() ? dayjsValue : null
        const resolvedValue = resolved ? resolved.format(format) : null
        onChange(resolvedValue)
        onEnterConfirm(resolvedValue)
        return
      }
      onKeyDown?.(e)
    }

    return (
      <DatePicker
        ref={ref}
        value={dayjsValue}
        onChange={handleChange}
        size={size}
        format={format}
        showTime={showTime}
        allowClear={allowClear}
        placeholder={placeholder}
        status={status}
        style={{ width: '100%' }}
        onKeyDown={handleKeyDown}
        autoFocus={autoFocus}
      />
    )
  }
)

DateInput.displayName = 'DateInput'
